import { type ReactNode, type RefObject, useCallback, useEffect, useMemo } from 'react';
import {
  type FieldValues,
  FormProvider,
  type SubmitErrorHandler,
  type SubmitHandler,
  type UseFormReturn,
} from 'react-hook-form';
import { type ObjectSchema } from 'yup';

import { FormProviderKnowMe } from '@/components/form/FormProviderKnowMe.comp.tsx';
import { GuardedFormContext } from '@/components/form/GuardedFormContext.ts';
import { UnsavedChangesDialog } from '@/components/ui/dialog/UnsavedChangesDialog.comp.tsx';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard.hook.ts';

export interface GuardedFormHandle {
  guardAction: (action: () => void) => void;
  submit: () => void;
}

interface GuardedFormProps<T extends FieldValues> {
  children: ReactNode;
  formMethods: UseFormReturn<T>;
  formProps?: Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'>;
  guardRef?: RefObject<GuardedFormHandle | null>;
  onSubmit: SubmitHandler<T>;
  onSubmitError?: SubmitErrorHandler<T>;
  validation?: ObjectSchema<object>;
}

export const GuardedForm = <T extends FieldValues>({
  children,
  formMethods,
  formProps,
  guardRef,
  onSubmit,
  onSubmitError,
  validation,
}: GuardedFormProps<T>) => {
  const { isDirty, isSubmitting } = formMethods.formState;

  useEffect(() => {
    formMethods.reset(formMethods.getValues());
  }, []);

  const {
    bypassGuard,
    guardAction,
    isDialogOpen,
    onStay,
    onSuccess: guardOnSuccess,
  } = useUnsavedChangesGuard(isDirty);

  const wrappedSubmit: SubmitHandler<T> = useCallback(
    async (data: T) => {
      bypassGuard();
      await onSubmit(data);
    },
    [bypassGuard, onSubmit],
  );

  const handleDialogSave = useCallback(() => {
    void formMethods.handleSubmit(wrappedSubmit, onStay)();
  }, [formMethods, onStay, wrappedSubmit]);

  useEffect(() => {
    if (!guardRef) {
      return;
    }

    guardRef.current = {
      guardAction,
      submit: handleDialogSave,
    };

    return () => {
      guardRef.current = null;
    };
  }, [guardAction, guardRef, handleDialogSave]);

  const contextValue = useMemo(() => ({ guardAction }), [guardAction]);

  const formContent = (
    <>
      <form {...formProps} onSubmit={formMethods.handleSubmit(wrappedSubmit, onSubmitError)}>
        {children}
      </form>
      {isDialogOpen && (
        <UnsavedChangesDialog
          isLoading={isSubmitting}
          onCancel={onStay}
          onDiscard={guardOnSuccess}
          onSave={handleDialogSave}
        />
      )}
    </>
  );

  return (
    <GuardedFormContext value={contextValue}>
      {validation ? (
        <FormProviderKnowMe {...formMethods} validation={validation}>
          {formContent}
        </FormProviderKnowMe>
      ) : (
        <FormProvider {...formMethods}>{formContent}</FormProvider>
      )}
    </GuardedFormContext>
  );
};
